/**
 * public-url — the single composer for a tenant-scoped public event/registration
 * URL, now backed by the contracted `getTenantPublicConfig` read (kit
 * ee7551b2 / REQ-011). Host resolution is owned by the data layer; the shell
 * global (`__STUDIO_PUBLIC_ROOT__`) fallback is DROPPED per the 2026-07-07
 * geo/settings handoff — `getTenantPublicConfig` is the single source of truth.
 *
 * `loadPublicConfig()` fetches once and caches; `publicHost()`/`publicUrl()`
 * read the cache synchronously (empty until the first load resolves — call
 * `useTenantPublicConfig()` in any screen that renders a public URL so the
 * value fills in). Prefer the contracted `getPublicEventPage().publicUrl` for a
 * specific event's canonical link; this composer is the tenant-level prefix +
 * slug display form.
 */
import { useEffect, useState } from "react";
import { getTenantPublicConfig, type StudioSettingsRequestOptions } from "@v0y4ge/api-client";
import { getActiveTenantSlug, onTenantChange, onTenantSwitching } from "../shell/access";

export type PublicHostPrefix = "attend" | "book" | "join" | "meet" | "public";

interface PublicConfig { publicHost: string; attendHost: string; rootDomain: string; prefixes?: string[]; }
const OPTS: StudioSettingsRequestOptions = {
  baseUrl: (typeof import.meta !== "undefined" && (import.meta as { env?: Record<string, string> }).env?.VITE_SETTINGS_API_URL) || "/api",
  surface: "settings",
};

let _cfg: PublicConfig | null = null;
let _loading = false;
const _subs = new Set<() => void>();
const _notify = () => _subs.forEach((f) => f());
onTenantSwitching(() => { _cfg = null; _loading = false; _notify(); });
onTenantChange(() => { loadPublicConfig(); });

/** Fetch the tenant public config once (idempotent); notifies subscribers on resolve. */
export function loadPublicConfig(): void {
  if (_cfg || _loading || typeof getTenantPublicConfig !== "function") return;
  _loading = true;
  (getTenantPublicConfig as (o: StudioSettingsRequestOptions) => Promise<unknown>)(OPTS)
    .then((r: unknown) => { const raw = r as Record<string, any>; _cfg = (raw?.data ?? raw) as PublicConfig; _notify(); })
    .catch((e: unknown) => console.warn("getTenantPublicConfig failed:", e))
    .finally(() => { _loading = false; });
}

/** React hook: ensures the config is loaded and re-renders when it arrives. */
export function useTenantPublicConfig(): PublicConfig | null {
  const [, force] = useState(0);
  useEffect(() => {
    const fn = () => force((n) => n + 1);
    _subs.add(fn);
    loadPublicConfig();
    return () => { _subs.delete(fn); };
  }, []);
  return _cfg;
}

export function slugify(s: string): string {
  return (s || "").toString().toLowerCase().trim()
    .replace(/[^a-z0-9\s-]/g, "").replace(/\s+/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "");
}

/** Awaitable, time-bounded version of loadPublicConfig — for callers that are
 *  about to COMPOSE a fallback URL outside React (no ancestor has warmed the
 *  cache via useTenantPublicConfig). Resolves as soon as the config lands, or
 *  after `timeoutMs` with whatever is cached (possibly null) — the composer
 *  then degrades exactly as before. Fixes the smoke-caught race where a popup
 *  briefly landed on `attend.your-workspace/<slug>` (2026-07-09 amendment). */
export function ensurePublicConfig(timeoutMs = 3000): Promise<PublicConfig | null> {
  if (_cfg) return Promise.resolve(_cfg);
  loadPublicConfig();
  return new Promise((resolve) => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const done = () => { _subs.delete(fn); if (timer) clearTimeout(timer); resolve(_cfg); };
    const fn = () => done();
    _subs.add(fn);
    timer = setTimeout(done, timeoutMs);
  });
}

/** The tenant-scoped public host, e.g. "attend.acme-live.v0y4ge.com".
 *  Sourced from the contracted config; composed from rootDomain + slug only if
 *  the config hasn't loaded yet (returns a bare `prefix.slug` when neither is
 *  available — never a hardcoded vanity domain). */
export function publicHost(prefix: PublicHostPrefix = "attend", tenantSlug?: string | null): string {
  loadPublicConfig();
  if (_cfg) {
    if (prefix === "attend" && _cfg.attendHost) return _cfg.attendHost;
    if (_cfg.rootDomain) {
      const t = (tenantSlug ?? getActiveTenantSlug()) || "";
      return t ? `${prefix}.${t}.${_cfg.rootDomain}` : (_cfg.publicHost || `${prefix}.${_cfg.rootDomain}`);
    }
    if (prefix === "attend" && _cfg.publicHost) return _cfg.publicHost;
  }
  const t = (tenantSlug ?? getActiveTenantSlug()) || "your-workspace";
  return `${prefix}.${t}`;
}

/** Full public URL for an event/page slug. `bare` omits the scheme for display chrome. */
export function publicUrl(slug: string, opts?: { prefix?: PublicHostPrefix; tenantSlug?: string | null; bare?: boolean }): string {
  const host = publicHost(opts?.prefix ?? "attend", opts?.tenantSlug);
  const path = `${host}/${slug || "event"}`;
  return opts?.bare ? path : `https://${path}`;
}

/** Compose from a display title (slugifies first). */
export function publicUrlForTitle(title: string, opts?: { prefix?: PublicHostPrefix; tenantSlug?: string | null; bare?: boolean }): string {
  return publicUrl(slugify(title), opts);
}
